import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {MachineQualityForProductionSchedule} from '@domain/production-schedule/machine-quality-for-production-schedule';
import {GeneralJuteSettings} from '@domain/textile-data/machine-quality/general-jute-settings';
import {AssertionUtils, BaseComponent, TranslateService} from '@vdw/angular-component-library';

@Component({
  selector: 'app-production-schedule-quality-linked-settings',
  templateUrl: './production-schedule-quality-linked-settings.component.html'
})
export class ProductionScheduleQualityLinkedSettingsComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() public isAddingProductionOrder: boolean;
  @Input() public juteGeneralSettings: GeneralJuteSettings;
  @Input() public machineQuality: MachineQualityForProductionSchedule;
  @Input() public areAllFieldsDisabled: boolean = false;

  public qualityLinkedSettingsForm: FormGroup<{
    repeatLastDesignToEndOfPath: FormControl<boolean>;
    addJuteSoPathsHaveEqualHeight: FormControl<boolean>;
    addAdditionalSidePattern: FormControl<boolean>;
    addConditionalDesigns: FormControl<boolean>;
  }>;

  public constructor(
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setFormFields();
    this.setFormValues();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (('juteGeneralSettings' in changes && !changes.juteGeneralSettings.isFirstChange()) || ('machineQuality' in changes && !changes.machineQuality.isFirstChange())) {
      this.setFormFields();
      this.setFormValues();
    }
  }

  public getGeneralJuteSettings(): GeneralJuteSettings {
    const repeatLastDesignToEndOfPath: boolean = this.qualityLinkedSettingsForm.get('repeatLastDesignToEndOfPath').value;
    const addJuteSoPathsHaveEqualHeight: boolean = this.qualityLinkedSettingsForm.get('addJuteSoPathsHaveEqualHeight').value;
    const addAdditionalSidePattern: boolean = this.qualityLinkedSettingsForm.get('addAdditionalSidePattern').value;
    const addConditionalDesigns: boolean = this.qualityLinkedSettingsForm.get('addConditionalDesigns').value;

    return {
      ...this.juteGeneralSettings,
      repeatLastDesignToEndOfPath: repeatLastDesignToEndOfPath,
      addJuteSoPathsHaveEqualHeight: addJuteSoPathsHaveEqualHeight,
      borderIndexColorToggle: addAdditionalSidePattern,
      addConditionalDesigns: addConditionalDesigns
    } as GeneralJuteSettings;
  }

  private setFormFields(): void {
    const hasJuteGeneralSettingsOnProductionOrder = !AssertionUtils.isNullOrUndefined(this.juteGeneralSettings);
    const hasJuteGeneralSettingsOnMachineQuality = !AssertionUtils.isNullOrUndefined(this.machineQuality.juteGeneralSettings);

    const addAdditionalSidePatternControl = this.formBuilder.control(false);
    const addJuteSoPathsHaveEqualHeightControl = this.formBuilder.control(false);
    const addConditionalDesignsControl = this.formBuilder.control(false);

    if (!this.getAddAdditionalSidePattern(hasJuteGeneralSettingsOnProductionOrder, hasJuteGeneralSettingsOnMachineQuality)) {
      addAdditionalSidePatternControl.disable();
    }

    if (!this.getAddJuteSoPathsHaveEqualHeight(hasJuteGeneralSettingsOnProductionOrder, hasJuteGeneralSettingsOnMachineQuality)) {
      addJuteSoPathsHaveEqualHeightControl.disable();
    }

    if (!this.getAddConditionalDesigns(hasJuteGeneralSettingsOnProductionOrder, hasJuteGeneralSettingsOnMachineQuality)) {
      addConditionalDesignsControl.disable();
    }

    this.qualityLinkedSettingsForm = this.formBuilder.group({
      repeatLastDesignToEndOfPath: this.formBuilder.control(false),
      addJuteSoPathsHaveEqualHeight: addJuteSoPathsHaveEqualHeightControl,
      addAdditionalSidePattern: addAdditionalSidePatternControl,
      addConditionalDesigns: addConditionalDesignsControl
    });

    if (this.areAllFieldsDisabled) {
      this.qualityLinkedSettingsForm.disable();
    }
  }

  private setFormValues(): void {
    const hasJuteGeneralSettingsOnProductionOrder = !AssertionUtils.isNullOrUndefined(this.juteGeneralSettings);
    const hasJuteGeneralSettingsOnMachineQuality = !AssertionUtils.isNullOrUndefined(this.machineQuality.juteGeneralSettings);

    this.qualityLinkedSettingsForm.reset({
      repeatLastDesignToEndOfPath: this.getRepeatLastDesignToEndOfPath(hasJuteGeneralSettingsOnProductionOrder, hasJuteGeneralSettingsOnMachineQuality),
      addJuteSoPathsHaveEqualHeight: this.getAddJuteSoPathsHaveEqualHeight(hasJuteGeneralSettingsOnProductionOrder, hasJuteGeneralSettingsOnMachineQuality),
      addAdditionalSidePattern: this.getAddAdditionalSidePattern(hasJuteGeneralSettingsOnProductionOrder, hasJuteGeneralSettingsOnMachineQuality),
      addConditionalDesigns: this.getAddConditionalDesigns(hasJuteGeneralSettingsOnProductionOrder, hasJuteGeneralSettingsOnMachineQuality)
    });
  }

  private getRepeatLastDesignToEndOfPath(hasJuteGeneralSettingsOnProductionOrder: boolean, hasJuteGeneralSettingsOnMachineQuality: boolean): boolean {
    if (hasJuteGeneralSettingsOnProductionOrder) {
      return this.juteGeneralSettings.repeatLastDesignToEndOfPath;
    } else if (hasJuteGeneralSettingsOnMachineQuality) {
      return this.machineQuality.juteGeneralSettings.repeatLastDesignToEndOfPath;
    } else {
      return false;
    }
  }

  private getAddJuteSoPathsHaveEqualHeight(hasJuteGeneralSettingsOnProductionOrder: boolean, hasJuteGeneralSettingsOnMachineQuality: boolean): boolean {
    if (hasJuteGeneralSettingsOnProductionOrder) {
      return this.juteGeneralSettings.addJuteSoPathsHaveEqualHeight;
    } else if (hasJuteGeneralSettingsOnMachineQuality) {
      return this.machineQuality.juteGeneralSettings.addJuteSoPathsHaveEqualHeight;
    } else {
      return false;
    }
  }

  private getAddAdditionalSidePattern(hasJuteGeneralSettingsOnProductionOrder: boolean, hasJuteGeneralSettingsOnMachineQuality: boolean): boolean {
    if (hasJuteGeneralSettingsOnProductionOrder) {
      return this.juteGeneralSettings.borderIndexColorToggle;
    } else if (hasJuteGeneralSettingsOnMachineQuality) {
      return this.machineQuality.juteGeneralSettings.borderIndexColorToggle;
    } else {
      return false;
    }
  }

  private getAddConditionalDesigns(hasJuteGeneralSettingsOnProductionOrder: boolean, hasJuteGeneralSettingsOnMachineQuality: boolean): boolean {
    if (this.isAddingProductionOrder && !hasJuteGeneralSettingsOnProductionOrder) {
      return this.machineQuality.juteConditions.length > 0;
    } else {
      let result = false;
      if (hasJuteGeneralSettingsOnProductionOrder) {
        result = this.juteGeneralSettings.addConditionalDesigns;
      } else if (hasJuteGeneralSettingsOnMachineQuality) {
        result = this.machineQuality.juteGeneralSettings.addConditionalDesigns ?? this.machineQuality.juteConditions.length > 0;
      }
      return result;
    }
  }
}
